/**
 * Auth Constants - Frontend
 * Must match backend enums
 */

export const UserRole = {
  SUPERADMIN: 'SUPERADMIN',
  USER: 'USER',
  PARTNER: 'PARTNER',
  PARTNER_USER: 'PARTNER_USER',
  SOLICITOR: 'SOLICITOR'
};

export const UserStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  BLOCKED: 'BLOCKED',
  DELETED: 'DELETED'
};

export const HierarchyLevel = {
  ROOT: 0,
  TIER_1: 1,
  TIER_2: 2,
  TIER_3: 3
};

/**
 * User role display labels
 */
export const ROLE_LABELS = {
  [UserRole.SUPERADMIN]: 'Super Admin',
  [UserRole.USER]: 'User',
  [UserRole.PARTNER]: 'Partner',
  [UserRole.PARTNER_USER]: 'Partner User',
  [UserRole.SOLICITOR]: 'Solicitor'
};

/**
 * Status display labels with colors
 */
export const STATUS_CONFIG = {
  [UserStatus.PENDING]: { label: 'Pending', color: 'warning', icon: '⏳' },
  [UserStatus.ACTIVE]: { label: 'Active', color: 'success', icon: '✓' },
  [UserStatus.INACTIVE]: { label: 'Inactive', color: 'secondary', icon: '⏸' },
  [UserStatus.BLOCKED]: { label: 'Blocked', color: 'danger', icon: '🚫' },
  [UserStatus.DELETED]: { label: 'Deleted', color: 'dark', icon: '🗑' }
};

/**
 * Who can create whom
 */
export const CREATION_PERMISSIONS = {
  [UserRole.SUPERADMIN]: [UserRole.USER, UserRole.PARTNER, UserRole.SOLICITOR],
  [UserRole.PARTNER]: [UserRole.PARTNER_USER],
  [UserRole.USER]: [],
  [UserRole.PARTNER_USER]: [],
  [UserRole.SOLICITOR]: []
};

/**
 * Status transitions allowed
 */
export const STATUS_TRANSITIONS = {
  [UserStatus.PENDING]: [UserStatus.ACTIVE, UserStatus.DELETED],
  [UserStatus.ACTIVE]: [UserStatus.INACTIVE, UserStatus.BLOCKED, UserStatus.DELETED],
  [UserStatus.INACTIVE]: [UserStatus.ACTIVE, UserStatus.BLOCKED, UserStatus.DELETED],
  [UserStatus.BLOCKED]: [UserStatus.ACTIVE, UserStatus.DELETED],
  [UserStatus.DELETED]: []
};

/**
 * Check if a role can create another role
 */
export function canCreateRole(creatorRole, targetRole) {
  const allowedRoles = CREATION_PERMISSIONS[creatorRole];
  return allowedRoles ? allowedRoles.includes(targetRole) : false;
}

/**
 * Check if status transition is allowed
 */
export function canTransitionStatus(fromStatus, toStatus) {
  const allowedTransitions = STATUS_TRANSITIONS[fromStatus];
  return allowedTransitions ? allowedTransitions.includes(toStatus) : false;
}

/**
 * Get allowed status transitions for current status
 */
export function getAllowedStatusTransitions(currentStatus) {
  return STATUS_TRANSITIONS[currentStatus] || [];
}

/**
 * Get role options for create user dropdown
 */
export function getRoleOptionsForUser(userRole) {
  const allowedRoles = CREATION_PERMISSIONS[userRole] || [];
  return allowedRoles.map(role => ({
    value: role,
    label: ROLE_LABELS[role]
  }));
}

/**
 * Check if user can login based on status
 */
export function canLogin(status) {
  return status === UserStatus.ACTIVE;
}

/**
 * Get hierarchy level for role
 */
export function getHierarchyLevel(role) {
  switch (role) {
    case UserRole.SUPERADMIN:
      return HierarchyLevel.ROOT;
    case UserRole.PARTNER:
    case UserRole.USER:
      return HierarchyLevel.TIER_1;
    case UserRole.PARTNER_USER:
    case UserRole.SOLICITOR:
      return HierarchyLevel.TIER_2;
    default:
      return HierarchyLevel.TIER_1;
  }
}
